import { CDSOption } from '@ciscodesignsystems/cds-react-select';
import _filter from 'lodash/filter';
import _mapValues from 'lodash/mapValues';
import _sortBy from 'lodash/sortBy';
import _uniqBy from 'lodash/uniqBy';
import { TFunction } from 'react-i18next';
import { IRole } from 'src/app/interfaces/IUserRoles';
import { isItManaged, isItManager } from 'src/app/utils/mspUtil';
import { IAssignedRolesAndInstances, IProductOption } from './interfaces';
import {
  IInputData,
  IMapGroupFormRow,
  IRowValidErrorState,
} from './MapIdentityProvidersGroups/interfaces';
import { MappingStatuses } from './MapIdentityProvidersGroups/MappingStatus';
import { IGroup } from './new-group/interfaces';
import { DefaultValue, GroupTabsList } from '../../enums/TabsList';
import { IUser } from '../../interfaces/ICommon';
import {
  IFormattedGroupUser,
  IGroupMapping,
  IGroupMappingWithName,
  IGroupRole,
  IGroupUser,
  IMSPOrgDetails,
  IOption as IGroupOption,
  IProduct,
} from '../../interfaces/IGroups';
import {
  ENTERPRISE_PRODUCT_TYPE,
  FEATURE_FLAG_CONSTANTS,
  READ_ONLY_GROUP_NAME,
  ENTERPRISE_PRODUCT_NAME,
} from '../../utils/constant';
import { getUIUserStatus } from '../../utils/shared';
import {
  IDependentOption,
  IdpUserTypeNames,
  IOption,
  IRbacGroup,
  IRolesRows,
} from '../users/interfaces';

const getIsGroupNameUnique = (
  allGroups: IGroup[],
  groupName: string
): boolean =>
  !allGroups.some(
    ({ name }) => name.trim().toLowerCase() === groupName.trim().toLowerCase()
  );

const extractProductsFromRoles = (assignedRoles: IGroupRole[]): IProduct[] => {
  return assignedRoles.reduce<IProduct[]>((unique, role) => {
    if (!unique.some((product) => product.value === role.product)) {
      unique.push({ label: role.product, value: role.product });
    }
    return unique;
  }, []);
};

const extractUsersOptions = (
  users: IUser[],
  groupUsers: IFormattedGroupUser[] = []
): CDSOption[] => {
  const filteredUsers = users.filter(
    (user) => !groupUsers.find((groupUser) => user.id === groupUser.userId)
  );
  return filteredUsers.map((user: IUser) => {
    const { firstName, lastName, email } = user?.profile;
    return {
      id: user.id,
      value: email ?? '',
      label: `${firstName} ${lastName} - ${email}`,
      selected: false,
      isSharedUser: (user as any)?.isSharedUser ?? false,
    };
  });
};

const combineWithSelectedOptions = (
  selectedOptions: CDSOption[],
  options: CDSOption[]
): CDSOption[] => {
  return [...selectedOptions, ...options].reduce(
    (acc: CDSOption[], current) => {
      const duplicateOption = acc.find(
        (option: CDSOption) => option.value === current.value
      );
      if (!duplicateOption) {
        acc.push(current);
      }
      return acc;
    },
    []
  );
};

const formatGroupUsers = (
  groupUsers: IGroupUser[],
  allUsers: IUser[]
): IFormattedGroupUser[] => {
  return groupUsers.map((groupUser) => {
    const userInfo = allUsers.find(
      (user) => groupUser.userId === user.id
    ) as IUser;

    const { userType, ...restGroupUser } = groupUser;

    return {
      ...restGroupUser,
      email: userInfo?.profile?.email,
      firstname: userInfo?.profile?.firstName,
      lastname: userInfo?.profile?.lastName,
      type:
        IdpUserTypeNames[userType as keyof typeof IdpUserTypeNames] || userType,
      isSharedUser: userInfo?.isSharedUser,
      status: getUIUserStatus(userInfo),
      lastLogin: userInfo?.lastLogin,
    };
  }) as IFormattedGroupUser[];
};

const formatGroupRoles = (
  assignedRoles: IGroupRole[],
  allRoles: IRole[]
): IGroupRole[] => {
  const matchedRoles = assignedRoles
    .map((assignedRole: IGroupRole) => {
      const matchedRole = allRoles.find(
        (role: IRole) => role.id === assignedRole.roleId
      );
      return matchedRole
        ? {
            ...assignedRole,
            id: assignedRole.id,
            role: matchedRole.roleDisplayName,
            product: matchedRole.product,
            type: matchedRole.type,
            productKey: matchedRole.productKey,
            delegatable: matchedRole.delegatable,
          }
        : false;
    })
    .filter(Boolean) as IGroupRole[];
  return _sortBy(matchedRoles, (role) => role.product);
};

const filterOptionByProduct = (
  selectedProductOption: CDSOption | undefined,
  option: CDSOption & { label: string },
  splitArg: string = ' '
): boolean | CDSOption => {
  const productName = option?.label?.split(splitArg).shift();
  const checkMatch = (selectedProduct: CDSOption): boolean =>
    selectedProduct.value === productName;

  const result = selectedProductOption
    ? checkMatch(selectedProductOption)
    : option;

  return result;
};

const filterOptionByAssignedRoles = (
  assignedRoles: IGroupRole[],
  option: CDSOption
): boolean => {
  const hasMatch = assignedRoles.find(
    (role: IGroupRole) => role.roleId === option.value
  );
  return !hasMatch;
};

const removeGroupRolesFromRolesOptions = (
  productRolesOptions: IDependentOption,
  rbacAssignedRoles: IGroupRole[]
): IDependentOption => {
  const filteredRoles = (rolesOptions: IOption[]): IOption[] =>
    _filter(rolesOptions, (rolesOption) => {
      return !rbacAssignedRoles
        .map((rbacAssignedRole) => rbacAssignedRole.roleId)
        .includes(rolesOption.value);
    });

  return _mapValues(productRolesOptions, (value) => filteredRoles(value));
};

const removeProductsOptionsWithoutUnassignedRoles = (
  productsOptions: IOption[],
  unassignedRolesOptions: IDependentOption
): IOption[] => {
  return productsOptions.filter((productsOption) =>
    Boolean(unassignedRolesOptions[productsOption.value]?.length)
  );
};

const getRoleName = (
  selectedProductsInAssignRolesDrawer: IRolesRows[]
): string | null => {
  const selectedProduct =
    selectedProductsInAssignRolesDrawer[0].selectedProduct;
  if (selectedProduct) {
    const selectedOption = selectedProductsInAssignRolesDrawer[0].rolesOptions[
      selectedProduct
    ].find((option: IOption) => option.selected);
    return selectedOption?.label ?? null;
  }
  return null;
};

const sliceTableData = <T>(
  pageIndex: number,
  pageSize: number,
  users: T[]
): T[] => {
  const from = pageIndex * pageSize;
  const to = (pageIndex + 1) * pageSize;
  return users.slice(from, to);
};

export const getNewStatusFromGroupOption = (
  item: IMapGroupFormRow,
  newSccGroupId: string,
  existingMapping?: IGroupMappingWithName
): MappingStatuses => {
  if (item.rowStatus === MappingStatuses.Added) {
    return MappingStatuses.Added;
  }

  if (!existingMapping) {
    return item.rowStatus as MappingStatuses;
  }

  const oldSccGroupId = existingMapping?.sccGroupId;
  const isRightChanged = newSccGroupId !== oldSccGroupId;

  if (isRightChanged) {
    if (item.rowStatus === MappingStatuses.LeftChanged) {
      return MappingStatuses.Changed;
    }

    return MappingStatuses.RightChanged;
  }

  if (item.rowStatus === MappingStatuses.Changed) {
    return MappingStatuses.LeftChanged;
  }

  return MappingStatuses.Unchanged;
};

export const getNewStatusFromIdpOption = (
  item: IMapGroupFormRow,
  newIdpId: string,
  existingMapping?: IGroupMappingWithName
): MappingStatuses => {
  if (item.rowStatus === MappingStatuses.Added) {
    return MappingStatuses.Added;
  }

  if (!existingMapping) {
    return item.rowStatus as MappingStatuses;
  }

  const oldIdpId = existingMapping?.idpId;
  const isRightChanged = newIdpId !== oldIdpId;

  if (isRightChanged) {
    if (item.rowStatus === MappingStatuses.LeftChanged) {
      return MappingStatuses.Changed;
    }

    return MappingStatuses.RightChanged;
  }

  if (item.rowStatus === MappingStatuses.Changed) {
    return MappingStatuses.LeftChanged;
  }

  return MappingStatuses.Unchanged;
};

export const selectIdpOption = (
  optionRows: IMapGroupFormRow[],
  selectedOption: IOption,
  currentRowIndex: number
): IMapGroupFormRow[] => {
  return checkForDuplicateIdpGroupNames(
    checkForDuplicateSccGroups(
      optionRows.reduce<IMapGroupFormRow[]>(
        (newRows, item: IMapGroupFormRow, index: number) => {
          if (currentRowIndex === index) {
            newRows.push({
              ...item,
              rowStatus: MappingStatuses.Added,
              idpName: {
                ...item.idpName,
                value: selectedOption.value,
                selectedOption,
              },
            });
            if (item.rowStatus !== MappingStatuses.Added) {
              newRows.push({
                ...item,
                rowStatus: MappingStatuses.Deleted,
                idpName: {
                  ...item.idpName,
                },
              });
            }
          } else {
            newRows.push(item);
          }
          return newRows;
        },
        []
      )
    )
  );
};

export const selectGroupOption = (
  optionRows: IMapGroupFormRow[],
  selectedOption: IOption,
  currentRowIndex: number,
  existingMappings: IGroupMappingWithName[]
): IMapGroupFormRow[] => {
  const existingMapping = existingMappings[currentRowIndex];

  return checkForDuplicateSccGroups(
    optionRows.map((item: IMapGroupFormRow, index: number) => {
      if (currentRowIndex === index) {
        return {
          ...item,
          rowStatus: getNewStatusFromGroupOption(
            item,
            selectedOption.value,
            existingMapping
          ),
          sccGroupName: {
            ...item.sccGroupName,
            value: selectedOption.value,
            selectedOption,
          },
        };
      }
      return item;
    })
  );
};

export const getNewStatusFromInput = (
  item: IMapGroupFormRow,
  newIdpGroupName: string,
  existingMapping?: IGroupMappingWithName
): MappingStatuses => {
  if (item.rowStatus === MappingStatuses.Added) {
    return MappingStatuses.Added;
  }

  if (!existingMapping) {
    return item.rowStatus as MappingStatuses;
  }

  const oldIdpGroupName = existingMapping.idpGroupName;
  const isLeftChanged = newIdpGroupName !== oldIdpGroupName;

  if (isLeftChanged) {
    if (item.rowStatus === MappingStatuses.RightChanged) {
      return MappingStatuses.Changed;
    }

    return MappingStatuses.LeftChanged;
  }

  if (item.rowStatus === MappingStatuses.Changed) {
    return MappingStatuses.RightChanged;
  }

  return MappingStatuses.Unchanged;
};

export const updateInputField = (
  rowsData: IMapGroupFormRow[] | any,
  inputData: IInputData,
  existingMappings: IGroupMappingWithName[]
): any => {
  const { value, name, rowIndex } = inputData;

  const existingMapping = existingMappings[rowIndex];

  return rowsData.map((row: any, index: number) => {
    if (index === rowIndex) {
      return {
        ...row,
        rowStatus: getNewStatusFromInput(row, value, existingMapping),
        [name]: { ...row[name], value },
      };
    }
    return row;
  });
};

export const getIsValidationByRegexpPassed = (value: string): boolean => {
  const fieldValidation = /^[a-zA-Z\d-_.=, ]+$/;
  return fieldValidation.test(value);
};

export const getIdpGroupNameErrorMessage = (value: string): string => {
  if (value.length === 0) {
    return 'noChars';
  }
  if (value.length > 128) {
    return 'moreThanLimitChars';
  }
  if (!getIsValidationByRegexpPassed(value)) {
    return 'regexpNotPassed';
  }
  return '';
};

export const checkForDuplicateIdpGroupNames = (
  rowData: IMapGroupFormRow[]
): IMapGroupFormRow[] => {
  const updatedRowsData = [...rowData];
  const getKey = (row: IMapGroupFormRow): string =>
    `${row.idpName.value}:${row.idpGroupName.value}`;

  const duplicateMap = updatedRowsData.reduce<Map<string, number[]>>(
    (duplicateCountMap, row, index) => {
      if (row.rowStatus !== MappingStatuses.Deleted) {
        duplicateCountMap.set(getKey(row), [
          ...(duplicateCountMap.get(getKey(row)) || []),
          index,
        ]);
      }
      return duplicateCountMap;
    },
    new Map<string, number[]>()
  );

  duplicateMap.forEach((values, key, map) => {
    if (values.length > 1) {
      values.forEach((index) => {
        updatedRowsData[index] = {
          ...updatedRowsData[index],
          idpGroupName: {
            ...updatedRowsData[index].idpGroupName,
            errorStatus: true,
            errorMessage: 'duplicateIdpGroupNames',
          },
        };
      });
    } else {
      updatedRowsData[values[0]] = {
        ...updatedRowsData[values[0]],
        idpGroupName: {
          ...updatedRowsData[values[0]].idpGroupName,
          errorStatus: false,
          errorMessage: '',
        },
      };
    }
  });

  return updatedRowsData;
};

export const checkForDuplicateSccGroups = (
  rowData: IMapGroupFormRow[]
): IMapGroupFormRow[] => {
  const updatedRowsData = [...rowData];

  const getKey = (row: IMapGroupFormRow): string => `${row.sccGroupName.value}`;

  const duplicateMap = updatedRowsData.reduce<Map<string, number[]>>(
    (duplicateCountMap, row, index) => {
      if (row.rowStatus !== MappingStatuses.Deleted) {
        duplicateCountMap.set(getKey(row), [
          ...(duplicateCountMap.get(getKey(row)) || []),
          index,
        ]);
      }
      return duplicateCountMap;
    },
    new Map<string, number[]>()
  );

  duplicateMap.forEach((values) => {
    if (values.length > 1) {
      values.forEach((index) => {
        updatedRowsData[index] = {
          ...updatedRowsData[index],
          sccGroupName: {
            ...updatedRowsData[index].sccGroupName,
            errorStatus: true,
            errorMessage: 'duplicateSccGroups',
          },
        };
      });
    } else {
      updatedRowsData[values[0]] = {
        ...updatedRowsData[values[0]],
        sccGroupName: {
          ...updatedRowsData[values[0]].sccGroupName,
          errorStatus: false,
          errorMessage: '',
        },
      };
    }
  });

  return updatedRowsData;
};

export const validateInputRowData = (
  rowsData: IMapGroupFormRow[] | any,
  inputData: IInputData
): any => {
  const { value, name, rowIndex } = inputData;
  const errorMessage = getIdpGroupNameErrorMessage(value);
  const errorStatus = Boolean(errorMessage);
  const updatedRowsData = [...rowsData];
  if (
    errorStatus &&
    updatedRowsData[rowIndex].rowStatus !== MappingStatuses.Deleted
  ) {
    updatedRowsData[rowIndex] = {
      ...updatedRowsData[rowIndex],
      [name]: {
        ...updatedRowsData[rowIndex][name],
        errorStatus,
        errorMessage,
      },
    };

    return updatedRowsData;
  }

  return checkForDuplicateIdpGroupNames(rowsData);
};

export const getOptionLabelByValue = (
  options: IOption[],
  value: string
): string => {
  const selectedOption = options.find(
    (option: IOption) => option.value === value
  );
  return selectedOption?.label ?? '';
};

export const updateRowDataWithRemoveStatus = (
  mapGroupsData: IMapGroupFormRow[],
  currentRow: number
): IMapGroupFormRow[] => {
  return checkForDuplicateIdpGroupNames(
    checkForDuplicateSccGroups(
      mapGroupsData.reduce<IMapGroupFormRow[]>((acc, row, index) => {
        if (index === currentRow) {
          if (row.rowStatus === MappingStatuses.Added) {
            return acc;
          } else {
            acc.push({ ...row, rowStatus: MappingStatuses.Deleted });
            return acc;
          }
        }

        acc.push(row);

        return acc;
      }, [])
    )
  );
};

export const formMappedGroupRow = (
  existingGroupMapping: IGroupMapping[],
  rbacGroupOptions: IGroupOption[],
  idpIdpOptions: IGroupOption[]
): IMapGroupFormRow[] =>
  existingGroupMapping.map((groupRow: IGroupMapping) => {
    return {
      idpName: {
        value: groupRow.idpId,
        selectedOption: {
          value: groupRow.idpId,
          label: getOptionLabelByValue(idpIdpOptions, groupRow.idpId),
          selected: true,
          errorStatus: false,
          errorMessage: '',
        },
        errorMessage: '',
        errorStatus: false,
      },
      idpGroupName: {
        value: groupRow.idpGroupName,
        errorStatus: false,
        errorMessage: '',
      },
      sccGroupName: {
        value: groupRow.rbacGroupId,
        selectedOption: {
          value: groupRow.rbacGroupId,
          label: getOptionLabelByValue(rbacGroupOptions, groupRow.rbacGroupId),
          selected: true,
          errorStatus: false,
          errorMessage: '',
        },
        errorMessage: '',
        errorStatus: false,
      },
      rowStatus: MappingStatuses.Unchanged,
    };
  });

export const getChangeSummaryTextKey = (status: MappingStatuses): string => {
  const statusSummaryMap: Record<MappingStatuses, string> = {
    [MappingStatuses.Added]: 'groups:mapIdpGroups.thirdStep.summary.added',
    [MappingStatuses.Deleted]: 'groups:mapIdpGroups.thirdStep.summary.deleted',
    [MappingStatuses.Unchanged]:
      'groups:mapIdpGroups.thirdStep.summary.unchanged',
    [MappingStatuses.Changed]: 'groups:mapIdpGroups.thirdStep.summary.changed',
    [MappingStatuses.LeftChanged]:
      'groups:mapIdpGroups.thirdStep.summary.leftChanged',
    [MappingStatuses.RightChanged]:
      'groups:mapIdpGroups.thirdStep.summary.rightChanged',
  };

  return statusSummaryMap[status];
};

export const getRowsState = (
  mapGroupsData: IMapGroupFormRow[],
  stepperMode: 'edit' | 'create'
): boolean => {
  const nonInitialRows = mapGroupsData.filter((g) => g.rowStatus);

  const hasOnlyNonDeletedEmptyRows = nonInitialRows.length
    ? !nonInitialRows.some((g) => g.sccGroupName.value && g.idpGroupName.value)
    : true;

  const rowsState = mapGroupsData.map((item: IMapGroupFormRow) => {
    return {
      error:
        item.rowStatus !== MappingStatuses.Deleted &&
        (item.idpGroupName.errorStatus ||
          item.sccGroupName.errorStatus ||
          item.idpName.errorStatus),
      isUnchanged: item.rowStatus === MappingStatuses.Unchanged,
    };
  });
  const isErrorFieldExist = rowsState.find(
    (item: IRowValidErrorState) => item.error
  );

  return (
    Boolean(isErrorFieldExist) ||
    (stepperMode === 'create' && hasOnlyNonDeletedEmptyRows)
  );
};

export const createInitialStateOptions = (
  t: TFunction<'translation', undefined>,
  hasManager: boolean
): CDSOption[] => {
  const initialState = [
    {
      label: t('groups:table.groupsOptions.allGroups'),
      value: DefaultValue.All_groups,
      selected: true,
    },
    {
      label: t('groups:table.groupsOptions.localGroups'),
      value: GroupTabsList.Local,
    },
    {
      label: t('groups:table.groupsOptions.federatedGroups'),
      value: GroupTabsList.Federated,
    },
    {
      label: t('groups:table.groupsOptions.systemGroups'),
      value: GroupTabsList.System,
    },
  ];

  const initialStateManagedOrg = [
    ...initialState,
    {
      label: t('groups:table.groupsOptions.sharedGroups'),
      value: GroupTabsList.Shared,
    },
  ];

  return hasManager ? initialStateManagedOrg : initialState;
};

export const getAssignedRolesAndInstancesIds = (
  assignedRoles: IGroupRole[]
): IAssignedRolesAndInstances[] =>
  assignedRoles.map((assignedRole) => {
    return {
      roleId: assignedRole.roleId,
      instanceId: assignedRole.tenantId,
    };
  });

export const getUnassignedInstances = (
  role: IDependentOption,
  assignedRolesAndInstancesIds: IAssignedRolesAndInstances[]
): IDependentOption[] =>
  role.instances.filter((instance: IDependentOption) => {
    return !assignedRolesAndInstancesIds.find((assignedRoleAndInstanceIds) => {
      return (
        assignedRoleAndInstanceIds.roleId === role.id &&
        (assignedRoleAndInstanceIds.instanceId === instance.id ||
          instance.id === ENTERPRISE_PRODUCT_TYPE)
      );
    });
  });

export const filterExcludedOptions = (
  options: CDSOption[],
  excludedOrgs: IMSPOrgDetails[]
): CDSOption[] => {
  const excludedIds = new Set(excludedOrgs.map((org) => org.id));
  return options.filter((org) => !excludedIds.has(org.value as string));
};

export const isRoleAcceptedWithoutInstance = (
  hasMspAssignRoleWithoutInstance: boolean,
  enterpriseType: string,
  filteredManagedOrsIds: string[],
  groupRole: IGroupRole | undefined,
  enterpriseId: string
): boolean => {
  return (
    hasMspAssignRoleWithoutInstance &&
    ((isItManager(enterpriseType) &&
      (filteredManagedOrsIds.includes(groupRole?.tenantId ?? '') ||
        filteredManagedOrsIds.includes(groupRole?.enterpriseId ?? '') ||
        groupRole?.enterpriseId === '00000000-0000-0000-0000-000000000000')) ||
      (isItManaged(enterpriseType) &&
        enterpriseId !== '' &&
        groupRole?.tenantId === enterpriseId))
  );
};

export function getAssignRoleRequestBody(
  roleId: string,
  tenant: { isOrgAdmin?: boolean; instanceId?: string } | undefined,
  isManagerOrg: boolean
): { roleId: string; tenantId?: string; AssignableWithoutInstance?: boolean } {
  const base = {
    roleId,
    ...(!tenant?.isOrgAdmin && { tenantId: tenant?.instanceId }),
  };
  if (isManagerOrg) {
    return {
      ...base,
      AssignableWithoutInstance: true,
    };
  }
  return base;
}

export const mapGroupTypeToTab = (
  groupType: string
): GroupTabsList | undefined => {
  switch (groupType) {
    case 'Local':
      return GroupTabsList.Local;
    case 'Federated':
      return GroupTabsList.Federated;
    case 'Shared':
      return GroupTabsList.Shared;
    case 'System':
      return GroupTabsList.System;
    default:
      return undefined;
  }
};

export const getFormattedTableData = (
  data: IRbacGroup[],
  hasFeatureFlag: any
): IRbacGroup[] => {
  const isReadOnlyGroupsFFEnabled = hasFeatureFlag(
    FEATURE_FLAG_CONSTANTS.SCC_READ_ONLY_GROUP
  );

  if (!isReadOnlyGroupsFFEnabled) {
    data = data.filter((group) => group.name !== READ_ONLY_GROUP_NAME);
  }

  return data.map((group) => ({
    ...group,
    description:
      group.description === null ||
      group.description === undefined ||
      group.description === ''
        ? '—'
        : group.description,
  }));
};

export const generateUniqueProductFilterOptions = (
  tableData: IGroupRole[]
): IProductOption[] =>
  _uniqBy(
    tableData.map(({ productName, productKey }: IGroupRole) => ({
      label:
        productKey === ENTERPRISE_PRODUCT_TYPE
          ? ENTERPRISE_PRODUCT_NAME
          : (productName ?? ''),
      value: productKey,
    })),
    'value'
  );

export const applyProductFilterAndNameMapping = (
  currentProductFilterOptions: IProductOption[],
  productFilterSelectedOption: IProductOption | null,
  productNameMap: Record<string, string>
): IProductOption[] => {
  const productOptionsForUpdate = productFilterSelectedOption
    ? currentProductFilterOptions?.map((option: IProductOption) => {
        if (productFilterSelectedOption.value === option.value) {
          return productFilterSelectedOption;
        }
        return option;
      })
    : currentProductFilterOptions;

  return productOptionsForUpdate.map((option: IProductOption) => {
    const productName = option.label ?? '';
    const adjustedProductName = productNameMap[productName] || productName;
    return {
      ...option,
      label: adjustedProductName,
    };
  });
};

export {
  combineWithSelectedOptions,
  extractProductsFromRoles,
  extractUsersOptions,
  filterOptionByAssignedRoles,
  filterOptionByProduct,
  formatGroupRoles,
  formatGroupUsers,
  getIsGroupNameUnique,
  getRoleName,
  removeGroupRolesFromRolesOptions,
  removeProductsOptionsWithoutUnassignedRoles,
  sliceTableData,
};
